/**
 * Hook for managing script preview state and operations.
 *
 * Handles script generation preview, regeneration, and approval flow
 * before proceeding with full video/audio generation.
 *
 * Token Billing:
 * - Requires authentication (uses useAuth hook)
 * - Free models (ending with :free) don't charge tokens
 * - Paid models are billed based on LLM usage
 */

import { useState, useCallback } from 'react';

import { ScriptPreviewService } from '../services/scriptPreview';
import { handleError } from '../utils/errorHandler';

import type {
  ScriptPreviewRequest,
  ScriptPreviewResponse,
  VideoConfig,
  AudioGenerationRequest,
} from '../types';

interface UseScriptPreviewReturn {
  /** Whether a preview is currently being generated */
  isLoading: boolean;
  /** The current script preview data */
  preview: ScriptPreviewResponse | null;
  /** Error message if preview generation failed */
  error: string | null;
  /** Whether the preview modal is open */
  isModalOpen: boolean;
  /** Generate a new script preview */
  generatePreview: (request: ScriptPreviewRequest) => Promise<void>;
  /** Regenerate the current preview with the same parameters */
  regeneratePreview: () => Promise<void>;
  /** Open the preview modal */
  openModal: () => void;
  /** Close the preview modal */
  closeModal: () => void;
  /** Reset all state */
  reset: () => void;
  /** Helper: Create preview request from video config */
  createVideoPreviewRequest: (config: VideoConfig) => ScriptPreviewRequest;
  /** Helper: Create preview request from audio config */
  createAudioPreviewRequest: (
    config: AudioGenerationRequest & { topic?: string }
  ) => ScriptPreviewRequest;
}

export const useScriptPreview = (): UseScriptPreviewReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [preview, setPreview] = useState<ScriptPreviewResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  /**
   * Generate a new script preview
   */
  const generatePreview = useCallback(async (request: ScriptPreviewRequest): Promise<void> => {
    setIsLoading(true);
    setError(null);
    setIsModalOpen(true);

    try {
      const response = await ScriptPreviewService.generatePreview(request);

      if (response.success) {
        setPreview(response);
      } else {
        setError(response.error || 'Failed to generate preview');
      }
    } catch (err) {
      const errorMessage = handleError(err, 'ScriptPreview', 'Failed to generate preview');
      setError(errorMessage);
      console.error('Script preview generation error:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Regenerate the current preview with the same parameters
   */
  const regeneratePreview = useCallback(async (): Promise<void> => {
    if (!preview?.preview_id) {
      setError('No preview to regenerate');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await ScriptPreviewService.regeneratePreview(preview.preview_id);

      if (response.success) {
        setPreview(response);
      } else {
        setError(response.error || 'Failed to regenerate preview');
      }
    } catch (err) {
      const errorMessage = handleError(err, 'ScriptPreview', 'Failed to regenerate preview');
      setError(errorMessage);
      console.error('Script preview regeneration error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [preview?.preview_id]);

  /**
   * Open the preview modal
   */
  const openModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  /**
   * Close the preview modal
   */
  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  /**
   * Reset all state
   */
  const reset = useCallback(() => {
    setIsLoading(false);
    setPreview(null);
    setError(null);
    setIsModalOpen(false);
  }, []);

  /**
   * Helper: Create preview request from video config
   */
  const createVideoPreviewRequest = useCallback((config: VideoConfig): ScriptPreviewRequest => {
    return ScriptPreviewService.createVideoPreviewRequest(config);
  }, []);

  /**
   * Helper: Create preview request from audio config
   */
  const createAudioPreviewRequest = useCallback(
    (config: AudioGenerationRequest & { topic?: string }): ScriptPreviewRequest => {
      return ScriptPreviewService.createAudioPreviewRequest(config);
    },
    []
  );

  return {
    isLoading,
    preview,
    error,
    isModalOpen,
    generatePreview,
    regeneratePreview,
    openModal,
    closeModal,
    reset,
    createVideoPreviewRequest,
    createAudioPreviewRequest,
  };
};

export default useScriptPreview;
